import Select, { StylesConfig, components, ControlProps, GroupBase } from "react-select";
import { User } from "../../../auth/types/User";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { toast } from "sonner";
import { useUser } from "../../../auth/hooks/useUser";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { IconSearch } from "@tabler/icons-react";

interface PropertySelectProps {
    user: User;
}

type OptionType = {
    value: string;
    label: string;
    inactive?: boolean;
};

const MAX_RECENT = 5;

const CustomControl = ({ children, ...props }: ControlProps<OptionType, false>) => (
    <components.Control {...props}>
        <div className="ps-2 d-flex align-items-center text-muted">
            <IconSearch size={18} />
        </div>
        {children}
    </components.Control>
);

const customStyles: StylesConfig<OptionType, false, GroupBase<OptionType>> = {
    control: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#FFFFFF' : '#f8f9fa', // Background change on focus
        border: state.isFocused ? '1px solid #007bff' : '1px solid transparent', // Show border on focus
        boxShadow: 'none', // Remove shadow on focus
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#FFFFFF',
            border: '1px solid #dee2e6', // Subtle border on hover
        },
        cursor: 'text',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        color: '#495057', // Dropdown indicator color
    }),
    indicatorSeparator: () => ({
        display: 'none', // Hide the separator next to the dropdown indicator
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#e9ecef' : undefined,
        color: state.isSelected ? 'white' : state.data.inactive ? '#868e96' : '#495057',
        padding: 10,
        '&:hover': {
            backgroundColor: '#e9ecef',
            color: '#007bff',
        },
    }),
    groupHeading: (provided) => ({
        ...provided,
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        color: '#adb5bd',
        padding: '8px 10px 4px',
        marginBottom: 0,
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#adb5bd',
        fontSize: '14px',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#495057',
        fontWeight: 500,
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,  // Pastikan z-index cukup tinggi
    }),
};

const PropertySelect: React.FC<PropertySelectProps> = ({ user }) => {
    const { refetch } = useUser();
    const [property, setProperty] = useState({ value: user?.active_property_id, label: user?.active_property });

    // Daftar "Recently Viewed" per-user, disimpan sebagai array ID (MRU, index 0 = paling baru).
    // Simpan ID saja — label di-resolve fresh dari user.properties saat render supaya tidak basi.
    const [recentIds, setRecentIds] = useLocalStorage<string[]>(`recent-properties:${user?.id ?? 'anon'}`, []);

    const queryClient = useQueryClient();

    const { mutate } = useMutation({
        mutationFn: (propertyId: string) => privateApi.post('/v3/change-property', { property_id: propertyId }),
        onSuccess: () => {
            refetch().then(async () => {
                await queryClient.refetchQueries();
                toast.success('Property changed successfully', { id: 'change-property' });
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, { id: 'change-property' });
        },
    });

    const handlePropertySelect = (selectedOption: any, propertyId: string) => {
        setProperty(selectedOption);
        toast.loading('Changing property...', { id: 'change-property' });
        mutate(propertyId);
    }

    useEffect(() => {
        if (user) {
            setProperty({ value: user.active_property_id, label: user.active_property });
        }
    }, [user]);

    // Rekam property aktif ke daftar recent setiap kali berubah. Ini otomatis meng-cover:
    // login pertama (seed property aktif) DAN setiap ganti property sukses (setelah refetch
    // meng-update active_property_id). Karena bergantung pada active_property_id — yang hanya
    // berubah setelah change-property SUKSES — switch yang gagal tidak mencemari daftar recent.
    useEffect(() => {
        const activeId = user?.active_property_id;
        if (!activeId) return;
        setRecentIds((prev) => [activeId, ...prev.filter((id) => id !== activeId)].slice(0, MAX_RECENT));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.active_property_id]);

    // Bangun opsi bergrup: "Recently Viewed" (MRU) di atas, "All Properties" (A-Z) di tengah,
    // "Inactive Properties" (A-Z) paling bawah. Property inactive HANYA muncul di grup terakhir
    // (tidak ikut Recent/All) supaya yang aktif selalu lebih mudah dijangkau.
    const inactiveIdSet = new Set(user?.inactive_property_ids ?? []);
    const allProps: OptionType[] = Object.entries(user?.properties || {}).map(([id, name]) => ({
        value: id,
        label: name,
        inactive: inactiveIdSet.has(id),
    }));

    const activeProps = allProps.filter((p) => !p.inactive);
    const inactiveProps = allProps
        .filter((p) => p.inactive)
        .sort((a, b) => a.label.localeCompare(b.label));

    const recentOptions: OptionType[] = recentIds
        .map((id) => activeProps.find((p) => p.value === id)) // resolve label fresh; buang id yang sudah tak ada / inactive
        .filter((p): p is OptionType => Boolean(p))
        .slice(0, MAX_RECENT);

    const recentIdSet = new Set(recentOptions.map((o) => o.value));
    const allSorted = activeProps
        .filter((p) => !recentIdSet.has(p.value)) // exclude yang sudah ada di Recent supaya tidak duplikat
        .sort((a, b) => a.label.localeCompare(b.label));

    const groupedOptions: GroupBase<OptionType>[] = [
        ...(recentOptions.length > 0 ? [{ label: 'Recently Viewed', options: recentOptions }] : []),
        ...(allSorted.length > 0 ? [{ label: 'All Properties', options: allSorted }] : []),
        ...(inactiveProps.length > 0 ? [{ label: 'Inactive Properties', options: inactiveProps }] : []),
    ];

    return (
        <div className="d-flex align-items-center w-100" style={{ maxWidth: '300px' }}>
            {/* Select */}
            <div className="flex-grow-1">
                <Select
                    components={{ Control: CustomControl }}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                    menuPosition="absolute"
                    value={property}
                    placeholder="Search properties..."
                    options={groupedOptions}
                    onChange={(selectedOption) => {
                        if (selectedOption) {
                            handlePropertySelect(selectedOption, selectedOption.value);
                        }
                    }}
                    className="border-0 w-100"
                />
            </div>
        </div>
    );
};

export default PropertySelect;
